import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CardContent,
  Card,
} from "@mui/material";
import mailService from "../services/mailService";
import { IEmail } from "../model/Email";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import SocialButtons from "./links/SocialButtons";

interface FormValues extends IEmail {
    subject?: string;
    name: string; 
    email: string; 
    phone?: string; 
    message: string;
    consent: boolean;
};


function ContactForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  function getSubject() {
    const subject = location.state?.subject 
      ? t("interestedIn") + location.state.subject
      : undefined;
    return subject;
  }

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    resetField,
    reset
  } = useForm<FormValues>({
    defaultValues: {
      subject: getSubject(),
      name: "",
      email: "",
      phone: "",
      message: "",
      consent: false
    }
  });

  useEffect(() => {
    const value = getSubject();
    resetField("subject", { defaultValue: value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const subject = watch("subject");

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    console.log(data);

    await mailService.sendMail(data);
    reset();

    navigate("/contact/sendsuccefully");
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Social Links */}
      <Box display="flex" justifyContent="center" sx={{ width: '100%' }}>
        <SocialButtons size="medium" subject={subject} />
      </Box>

      {/* Form */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {
          /* Subject */
          !!subject && (
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('form.subject')}
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled
                  sx={{
                    // Dark background
                    '& .MuiInputBase-root': {
                      backgroundColor: '#d4d4d4ff', // dark gray
                      color: '#fff',           // text color
                    },
                    // Dark label when disabled
                    '& .MuiInputLabel-root': {
                      color: '#bbb',
                    },
                    '& .Mui-disabled': {
                      color: '#fff',           // text when disabled
                    }
                  }}
                />
              )}
            />
          )  
        }

        {/* Name */}
        <Controller
          name="name"
          control={control}
          rules={{ required: t('form.required') }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('form.name')}
              margin="normal"
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={process.env.REACT_APP_CONTACT_FORM_DISABLED}
            />
          )}
        />

        {/* Email */}
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('form.required'),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t('form.invalidEmail')
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('form.email')}
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={process.env.REACT_APP_CONTACT_FORM_DISABLED}
            />
          )}
        />

        {/* Phone */}
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('form.phone')}
              margin="normal"
              disabled={process.env.REACT_APP_CONTACT_FORM_DISABLED}
            />
          )}
        />

        {/* Message */}
        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('form.message')}
              multiline
              rows={8}
              margin="normal"
              disabled={process.env.REACT_APP_CONTACT_FORM_DISABLED}
            />
          )}
        />

        {/* Consent */}
        <Card elevation={3} sx={{ textAlign: 'left', mt: 2 }}>
          <CardContent>
            <Controller
              name="consent"
              control={control}
              rules={{ required: t('form.consentRequired') }}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={process.env.REACT_APP_CONTACT_FORM_DISABLED} />}
                  sx={{ alignItems: 'center' }}
                  label={
                    <Typography variant="body2" sx={{ fontSize: 12, textAlign: 'left' }}>
                      {t('form.consent')}
                    </Typography>
                  }
                />
              )}
            />
            {errors.consent && (
              <Typography color="error" variant="body2">
                {errors.consent.message}
              </Typography>
            )}

            {/* Privacy Notice */}
            <Typography variant="body2" sx={{ mt: 2, fontSize: 10 }}>
              {t('form.privacyNotice')}
            </Typography>
          </CardContent>
        </Card>

        <Button type="submit" variant="contained" loading={isSubmitting} disabled={process.env.REACT_APP_CONTACT_FORM_DISABLED || isSubmitting} sx={{ mt: 2 }}>
          {t('form.submit')}
        </Button>
      </Box>
    </Box>
  );
}

export default ContactForm;